import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

/**
 * TreeRail — collapsible categories with counted child items (T-0140).
 *
 * Mirrors `.rail-item` / `.rail-cat` in
 * `docs/requirement-library/prototype/selection.css`: an "all" row, then
 * uppercase category headers that collapse, each holding items with a
 * mono count. The active item gets a paper-0 fill and an amber inset bar.
 * Items can show a small amber dot (for example "has a draft") and can
 * dim when their count is zero.
 *
 * Which item is active is controlled (`activeId` / `onSelect`). Which
 * categories are collapsed is UI state kept here, seeded by
 * `defaultCollapsedIds`.
 *
 * Accessibility: `role="tree"`; categories are `treeitem`s with
 * `aria-expanded`, items are `treeitem`s with `aria-selected`. Every row
 * is a real button. Up / Down move between visible rows, Left / Right
 * collapse / expand a category, Home / End jump to the ends.
 */

export interface TreeRailItem {
  id: string;
  label: string;
  count?: number;
  /** Leading 14px icon, for example an archive glyph on "Removed". */
  icon?: React.ReactNode;
  /** Small amber indicator dot before the count. */
  dot?: boolean;
  /** Accessible description of the dot, for example "Draft in progress". */
  dotLabel?: string;
  /** Short mono text shown in place of the count, for example a version "v2". */
  meta?: string;
}

export interface TreeRailCategory {
  id: string;
  label: string;
  count?: number;
  items: ReadonlyArray<TreeRailItem>;
  /**
   * Controls for the category itself (T-0157), for example a small menu
   * with Retire. Rendered after the header, shown when the row is hovered
   * or anything in it has focus, so it stays reachable with Tab. It must
   * not be a tree row: arrow keys skip it.
   */
  actions?: React.ReactNode;
}

export interface TreeRailProps {
  categories: ReadonlyArray<TreeRailCategory>;
  /** The "all" row. Omit to hide it. Selecting it reports `id`. */
  all?: { id: string; label: string; count?: number };
  /**
   * Top-level rows with no category (T-0184), for a flat list such as the
   * discovery frameworks. Shown after `all` and before the categories.
   */
  items?: ReadonlyArray<TreeRailItem>;
  /**
   * Items listed after the categories, under a separator: views that are
   * not a category member, for example "Removed". Selecting one reports
   * its `id` like any other item.
   */
  footerItems?: ReadonlyArray<TreeRailItem>;
  activeId: string | null;
  onSelect: (id: string) => void;
  /** Dim items (label and count) whose count is zero. */
  dimZero?: boolean;
  defaultCollapsedIds?: ReadonlyArray<string>;
  /** Accessible name for the tree. */
  'aria-label': string;
  className?: string;
}

const ROW_FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600';

export function TreeRail({
  categories,
  all,
  items,
  footerItems,
  activeId,
  onSelect,
  dimZero = false,
  defaultCollapsedIds,
  'aria-label': ariaLabel,
  className,
}: TreeRailProps) {
  const [collapsed, setCollapsed] = React.useState<ReadonlySet<string>>(
    () => new Set(defaultCollapsedIds ?? []),
  );
  const treeRef = React.useRef<HTMLDivElement>(null);

  const setOpen = (id: string, open: boolean) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (open) next.delete(id);
      else next.add(id);
      return next;
    });

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (!target.matches('[data-rail-row]')) return;
    const rows = Array.from(
      treeRef.current?.querySelectorAll<HTMLElement>('[data-rail-row]') ?? [],
    );
    const i = rows.indexOf(target);
    const catId = target.dataset.railCat;
    let next: HTMLElement | undefined;
    switch (e.key) {
      case 'ArrowDown':
        next = rows[Math.min(i + 1, rows.length - 1)];
        break;
      case 'ArrowUp':
        next = rows[Math.max(i - 1, 0)];
        break;
      case 'Home':
        next = rows[0];
        break;
      case 'End':
        next = rows[rows.length - 1];
        break;
      case 'ArrowLeft':
        if (!catId) return;
        setOpen(catId, false);
        break;
      case 'ArrowRight':
        if (!catId) return;
        setOpen(catId, true);
        break;
      default:
        return;
    }
    e.preventDefault();
    next?.focus();
  };

  return (
    <div
      ref={treeRef}
      role="tree"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className={className}
    >
      {all ? (
        <RailRow
          level={1}
          label={all.label}
          count={all.count}
          active={activeId === all.id}
          dim={false}
          onClick={() => onSelect(all.id)}
        />
      ) : null}
      {items?.map((item) => (
        <RailRow
          key={item.id}
          level={1}
          label={item.label}
          count={item.count}
          meta={item.meta}
          icon={item.icon}
          dot={item.dot}
          dotLabel={item.dotLabel}
          active={activeId === item.id}
          dim={dimZero && item.count === 0}
          onClick={() => onSelect(item.id)}
        />
      ))}
      {categories.map((cat) => {
        const open = !collapsed.has(cat.id);
        return (
          <div key={cat.id} role="none">
            <div role="none" className="group/cat flex items-end">
              <button
                type="button"
                role="treeitem"
                aria-level={1}
                aria-expanded={open}
                data-rail-row=""
                data-rail-cat={cat.id}
                onClick={() => setOpen(cat.id, !open)}
                className={cn(
                  'flex min-w-0 flex-1 items-center gap-1 rounded-chip px-2 pb-1 pt-3.5 text-left text-meta font-medium uppercase tracking-wide text-ink-500',
                  ROW_FOCUS,
                )}
              >
                <ChevronDown
                  className={cn(
                    'h-3 w-3 flex-none text-ink-300 transition-transform',
                    !open && '-rotate-90',
                  )}
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1 truncate">{cat.label}</span>
                {cat.count != null ? (
                  <span className="font-mono font-normal normal-case tracking-normal">
                    {cat.count.toLocaleString()}
                  </span>
                ) : null}
              </button>
              {cat.actions ? (
                <span
                  data-rail-actions=""
                  className="flex-none pb-0.5 opacity-0 focus-within:opacity-100 group-hover/cat:opacity-100 has-[[data-state=open]]:opacity-100"
                >
                  {cat.actions}
                </span>
              ) : null}
            </div>
            {open ? (
              <div role="group">
                {cat.items.map((item) => (
                  <RailRow
                    key={item.id}
                    level={2}
                    label={item.label}
                    count={item.count}
                    dot={item.dot}
                    dotLabel={item.dotLabel}
                    active={activeId === item.id}
                    dim={dimZero && item.count === 0}
                    onClick={() => onSelect(item.id)}
                  />
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
      {footerItems && footerItems.length > 0 ? (
        <>
          <div role="separator" className="mx-2 my-3 h-px bg-ink-100" />
          {footerItems.map((item) => (
            <RailRow
              key={item.id}
              level={1}
              label={item.label}
              count={item.count}
              icon={item.icon}
              active={activeId === item.id}
              dim={false}
              onClick={() => onSelect(item.id)}
            />
          ))}
        </>
      ) : null}
    </div>
  );
}

TreeRail.displayName = 'TreeRail';

interface RailRowProps {
  level: number;
  label: string;
  count?: number;
  meta?: string;
  icon?: React.ReactNode;
  dot?: boolean;
  dotLabel?: string;
  active: boolean;
  dim: boolean;
  onClick: () => void;
}

function RailRow({
  level,
  label,
  count,
  meta,
  icon,
  dot,
  dotLabel,
  active,
  dim,
  onClick,
}: RailRowProps) {
  return (
    <button
      type="button"
      role="treeitem"
      aria-level={level}
      aria-selected={active}
      data-rail-row=""
      onClick={onClick}
      className={cn(
        'flex h-[30px] w-full items-center gap-2 rounded-chip border px-2 text-left text-body-sm font-medium',
        ROW_FOCUS,
        active
          ? 'border-ink-100 bg-paper-0 font-semibold text-ink-900 shadow-[inset_2px_0_0_var(--accent-amber-600)]'
          : 'border-transparent hover:bg-paper-100',
        !active && (dim ? 'text-ink-300' : 'text-ink-700'),
      )}
    >
      {icon ? (
        <span className="flex-none text-ink-500" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {dot ? (
        <span className="h-1.5 w-1.5 flex-none rounded-full bg-accent-amber-600" title={dotLabel}>
          {dotLabel ? <span className="sr-only">{dotLabel}</span> : null}
        </span>
      ) : null}
      {meta != null || count != null ? (
        <span
          className={cn('font-mono text-meta font-normal', dim ? 'text-ink-300' : 'text-ink-500')}
        >
          {meta ?? count?.toLocaleString()}
        </span>
      ) : null}
    </button>
  );
}
